import {Injectable, OnDestroy, Optional} from '@angular/core';
import {BehaviorSubject, Observable, ReplaySubject, Subscription} from 'rxjs';
import {distinctUntilChanged, map} from 'rxjs/operators';

import {EditorRoutingService} from '@management/editor/editor-routing.service';
import {BucketChannelService} from '@management/base/services/bucket-channel/bucket-channel.service';
import {SubscribedUser} from '@paperlessio/sdk/api/models';
import {SelectedBlocksChangedItem} from '@management/base/services/channel/channel.type';
import {BlockSelection} from '@blocks/services/block-selection/block-selection.type';
import {BlockStore} from '@blocks/block/block.store';

/**
 * The Block Selection Service listens to the incoming list of editors (subscribed users) who work on a document simultaneously.
 * A second channel is open to the list of selected blocks, which is combined with the subscribed users through the subscription_uuid
 * key. The result is broadcast and can be used to highlight the selected blocks in certain colors and add additional information
 * to them (selected by which user, email etc).
 */
@Injectable({
  providedIn: 'root'
})
export class BlockSelectionService implements OnDestroy {
  public blockSelection$: ReplaySubject<BlockSelection[]> = new ReplaySubject<BlockSelection[]>(1);

  private subs: Subscription = new Subscription();
  private subscribedUsers: SubscribedUser[];
  private selectedBlocksChangedItems: SelectedBlocksChangedItem[];
  private pageEditorsSubject: BehaviorSubject<SubscribedUser[]> = new BehaviorSubject<SubscribedUser[]>(null);
  public pageEditor$: Observable<SubscribedUser[]> = this.pageEditorsSubject.asObservable();

  constructor(@Optional() private editorRoutingService: EditorRoutingService,
              private bucketChannelService: BucketChannelService,
              private blockStore: BlockStore) {
    this.subscribeToEditorsChangedEvents();
    this.subscribeToSelectecBlocksChangedEvents();
    if (editorRoutingService) {
      this.subscribeToOwnUserSelectionChangedEvents();
    }
  }

  private subscribeToOwnUserSelectionChangedEvents() {
    this.subs.add(this.editorRoutingService.changes
      .pipe(
        map(() => this.editorRoutingService.selectedBlock),
        distinctUntilChanged(),
      )
      .subscribe(this.onOwnUserSelectedBlocksChanged));
  }

  private subscribeToSelectecBlocksChangedEvents() {
    this.subs.add(this.bucketChannelService.selectedBlocksChangedSubject.subscribe(this.onSelectedBlockChanged));
  }

  private subscribeToEditorsChangedEvents() {
    this.subs.add(this.bucketChannelService.editorsChangedSubject.subscribe(this.onEditorsChanged));
  }

  private onOwnUserSelectedBlocksChanged = (selectedBlock) => {
    const selectedBlocks = selectedBlock > 0 ? [selectedBlock] : []; // see get selectedBlock() in EditorRoutingService: null returns -1
    this.bucketChannelService.sendSelectedBlocks(selectedBlocks);
  }

  private onEditorsChanged = (subscribedUsers: SubscribedUser[]) => {
    this.subscribedUsers = subscribedUsers;
    this.emitBlockSelection(this.createBlockSelection(this.subscribedUsers, this.selectedBlocksChangedItems))
  }

  private onSelectedBlockChanged = (selectedBlocksChangedItems: SelectedBlocksChangedItem[]) => {
    this.selectedBlocksChangedItems = selectedBlocksChangedItems;
    this.emitBlockSelection(this.createBlockSelection(this.subscribedUsers, this.selectedBlocksChangedItems))
  }

  private emitBlockSelection(blockSelections: BlockSelection[]) {
    this.blockSelection$.next([...blockSelections]);
    if (!(blockSelections?.length > 0)) {
      this.pageEditorsSubject.next(null);
    }
  }

  private filterOtherUsers = (subscribedUser: SubscribedUser) =>
    subscribedUser.subscription_uuid !== this.bucketChannelService.subscription_uuid;

  private createBlockSelection(subscribedUsers: SubscribedUser[], selectedBlocksChangedItems: SelectedBlocksChangedItem[]) {
    const blockSelections: BlockSelection[] = [];
    subscribedUsers = subscribedUsers || [];
    selectedBlocksChangedItems = selectedBlocksChangedItems || [];

    subscribedUsers
      .filter(this.filterOtherUsers)
      .map((subscribedUser: SubscribedUser) => {
        subscribedUser.selected_page_ids = [];
        return subscribedUser;
      })
      .map((subscribedUser: SubscribedUser) =>
        selectedBlocksChangedItems.map((selectedBlocksChangedItem: SelectedBlocksChangedItem) => {
            if (subscribedUser.subscription_uuid === selectedBlocksChangedItem.subscription_uuid) {
              const selected_page_ids: number[] = selectedBlocksChangedItem.selected_block_ids.map(
                (block_Id: number) => this.blockStore.getParentPageId(block_Id)
              );
              subscribedUser.selected_page_ids = selected_page_ids;
              const blockSelection = new BlockSelection(subscribedUser, selectedBlocksChangedItem, selected_page_ids)
              blockSelections.push(blockSelection);
            }
          }
        )
      )

    this.pageEditorsSubject.next([...subscribedUsers]);
    return blockSelections;
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }
}
